import { inject, Injectable } from '@angular/core';
import { ApiClientConstant } from '@cureskin/api-client/src/constant/api-client.constant';
import { ReorderKitBannerOutput, ReorderKitDetail } from '@cureskin/api-client/src/server-api';
import { ConnectionService } from '@services/connection-service';
import { ExperimentService } from '@services/experiment';
import { UserService } from '@services/user';

@Injectable({ providedIn: 'root' })
export class RegimenReorderService {

  connectionService = inject<ConnectionService>(ConnectionService);
  experimentService = inject<ExperimentService>(ExperimentService);
  userService = inject<UserService>(UserService);
  regimenReorderKitDetails: ReorderKitDetail[] = [];
  private cashUsedPerRegimen: Record<string, number> = {};
  private originalDiscountPerRegimen: Record<string, number> = {};

  async shouldShowReorderBanner(): Promise<boolean> {
    const user = this.connectionService.getActingUser();
    const isUnpaidUser = this.userService.isUnpaidUser(user);
    if (!this.experimentService.regimenReorderExperiment?.enable || isUnpaidUser) {
      return false;
    }
    const  { data } = await this.connectionService.showReOrderBanner();
    this.setBannerData(data);
    if (data?.showBanner) {
      await this.applyCashDeduction();
    }
    return data?.showBanner;
  }

  private setBannerData(data: ReorderKitBannerOutput): void {
    const details = data?.showBanner ? data?.reorderKitDetails ?? [] : [];
    this.regimenReorderKitDetails = this.sortFaceBeforeHair(details);
  }

  private sortFaceBeforeHair(details: ReorderKitDetail[]): ReorderKitDetail[] {
    const face = details.filter(d => d.regimenClass === ApiClientConstant.Regimen.Class.FACE);
    const hair = details.filter(d => d.regimenClass === ApiClientConstant.Regimen.Class.HAIR);
    return [...face, ...hair];
  }

  getRegimenReorderKitDetailsForClass(regimenClass: string): ReorderKitDetail {
    if (!regimenClass || !this.regimenReorderKitDetails.length) {
      return undefined;
    }
    return this.regimenReorderKitDetails.find((banner: ReorderKitDetail) => banner.regimenClass?.toLowerCase() === regimenClass.toLowerCase());
  }

  getRegimenReorderKitDetials(): ReorderKitDetail[] {
    return this.regimenReorderKitDetails || [];
  }

  getCashUsedForClass(regimenClass: string): number {
    return this.cashUsedPerRegimen[regimenClass] || 0;
  }

  getOriginalDiscountForClass(regimenClass: string): number {
    return this.originalDiscountPerRegimen[regimenClass] || 0;
  }

  private async applyCashDeduction(): Promise<void> {
    this.regimenReorderKitDetails.forEach((detail) => {
      this.originalDiscountPerRegimen[detail.regimenClass] = detail.totalMrp - detail.totalSP;
    });

    const cashBalance = await this.connectionService.fetchCashBalance();
    if (!cashBalance || cashBalance <= 0) return;

    this.regimenReorderKitDetails.forEach((detail) => {
      const cashUsed = Math.min(cashBalance, detail.totalSP);
      detail.totalSP = detail.totalSP - cashUsed;

      detail.discountInRupees = detail.totalMrp - detail.totalSP;
      detail.discountInPercentage = detail.totalMrp > 0
        ? Math.round((detail.discountInRupees / detail.totalMrp) * 100) : 0;

      this.cashUsedPerRegimen[detail.regimenClass] = cashUsed;
    });
  }

}
